import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './input';
import { Button } from './button';
import { 
  Search, 
  X, 
  FileText, 
  FolderOpen, 
  Users, 
  MessageSquare,
  Loader2,
  Command,
  BookOpen,
  GraduationCap,
} from 'lucide-react';
import { searchAPI } from '../../lib/api';

const GlobalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) onClose();
      }
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      return;
    }
    const searchTimeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchAPI.globalSearch(query);
        setResults(res.data.results);
      } catch (error) {
        console.error('Search error:', error);
        setResults({ projets: [], fichiers: [], messages: [], services: [], formations: [] });
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchTimeout);
  }, [query]);

  const allResults = results ? [
    ...results.fichiers.map(f => ({ type: 'file', data: f })),
    ...results.projets.map(p => ({ type: 'project', data: p })),
    ...results.services.map(s => ({ type: 'service', data: s })),
    ...results.formations.map(f => ({ type: 'formation', data: f })),
    ...results.messages.map(m => ({ type: 'message', data: m })),
  ] : [];

  const handleSelect = (item) => {
    switch (item.type) {
      case 'file':
        navigate(`/portail/projets/${item.data.project_id}`);
        break;
      case 'project':
        navigate(`/portail/projets/${item.data.id}`);
        break;
      case 'service':
        navigate(`/services`);
        break;
      case 'formation':
        navigate(`/services`);
        break;
      case 'message':
        navigate(`/portail/messages`);
        break;
      default:
        break;
    }
    onClose();
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && allResults[selectedIndex]) {
      handleSelect(allResults[selectedIndex]);
    }
  };

  if (!isOpen) return null;

  const iconMap = {
    file: FileText,
    project: FolderOpen,
    service: BookOpen,
    formation: GraduationCap,
    message: MessageSquare,
  };

  const labelMap = {
    file: 'Fichiers',
    project: 'Projets',
    service: 'Services',
    formation: 'Formations',
    message: 'Messages',
  };

  const groupedResults = {};
  allResults.forEach((r, idx) => {
    if (!groupedResults[r.type]) groupedResults[r.type] = [];
    groupedResults[r.type].push({ ...r, globalIdx: idx });
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl mx-4 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
          <Search className="w-5 h-5 text-slate-400" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Rechercher fichiers, projets, services..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            className="flex-1 border-0 shadow-none focus-visible:ring-0 text-lg"
            data-testid="global-search-input"
          />
          {loading ? (
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
          ) : query && (
            <Button variant="ghost" size="sm" onClick={() => setQuery('')}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {query && !loading && allResults.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun resultat pour "{query}"</p>
            </div>
          )}

          {Object.entries(groupedResults).map(([type, items]) => {
            const Icon = iconMap[type] || FileText;
            return (
              <div key={type} className="p-2 border-t border-slate-100 dark:border-slate-800 first:border-t-0">
                <p className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase">{labelMap[type] || type}</p>
                {items.map((item) => (
                  <button
                    key={`${type}-${item.globalIdx}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedIndex === item.globalIdx ? 'bg-primary/10 text-primary' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                    onClick={() => handleSelect(item)}
                    data-testid={`search-result-${type}-${item.globalIdx}`}
                  >
                    <Icon className="w-4 h-4 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {item.data.nom || item.data.nom_fichier || item.data.title || item.data.contenu}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {item.data.client_nom || item.data.project_nom || item.data.short_description || item.data.short_intro || item.data.sender_nom || ''}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">&#8593;&#8595;</kbd>
              naviguer
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">&#8629;</kbd>
              selectionner
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">esc</kbd>
            fermer
          </span>
        </div>
      </div>
    </div>
  );
};

const SearchButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
      data-testid="global-search-btn"
    >
      <Search className="w-4 h-4" />
      <span className="hidden sm:inline">Rechercher...</span>
      <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-slate-200 dark:bg-slate-700 rounded">
        <Command className="w-3 h-3" />K
      </kbd>
    </button>
  );
};

export { GlobalSearch, SearchButton };
